import { Link, useLocation } from 'react-router-dom'
import {
  HomeIcon,
  BanknotesIcon as BanknotesIconNav,
  BanknotesIcon,
  BookOpenIcon,
  ChartBarIcon,
  DocumentCheckIcon,
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  UsersIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  TagIcon,
  CubeIcon,
  SparklesIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import AgentChat from '../agents/AgentChat'
import FiltroPeriodo from '../common/FiltroPeriodo'
import { NOMBRE_ROL } from '../../config/permisos'
import { useUltimaActualizacion } from '../../hooks/useCfoData'

// "hoy 5:00 a. m." / "ayer 5:00 a. m." / "31 oct, 5:00 a. m." en hora de Guatemala
function textoActualizacion(iso) {
  const f = new Date(iso)
  const tz = 'America/Guatemala'
  const dia = (d) => d.toLocaleDateString('en-CA', { timeZone: tz })
  const hora = f.toLocaleTimeString('es-GT', { timeZone: tz, hour: 'numeric', minute: '2-digit' })
  const hoy = new Date()
  if (dia(f) === dia(hoy)) return `hoy ${hora}`
  if (dia(f) === dia(new Date(hoy.getTime() - 864e5))) return `ayer ${hora}`
  return `${f.toLocaleDateString('es-GT', { timeZone: tz, day: 'numeric', month: 'short' })}, ${hora}`
}

function UltimaActualizacion() {
  const { data } = useUltimaActualizacion()
  const ultima = data?.data?.ultima
  if (!ultima) return null
  return (
    <div className="hidden md:flex items-center gap-1.5 text-xs text-[var(--text-muted)]"
      title={`Última carga completa de datos del ERP: ${new Date(ultima).toLocaleString('es-GT', { timeZone: 'America/Guatemala', dateStyle: 'long', timeStyle: 'short' })}`}>
      <ArrowPathIcon className="w-3.5 h-3.5" />
      <span>Actualizado {textoActualizacion(ultima)}</span>
    </div>
  )
}

const navigation = [
  { name: 'Panel', href: '/', icon: HomeIcon },
  { name: 'Ventas', href: '/ventas', icon: ShoppingBagIcon },
  {
    name: 'Gastos Operativos', href: '/gastos-operativos', icon: BanknotesIconNav,
    children: [
      { name: 'Administración', href: '/gastos-operativos/administracion' },
    ],
  },
  { name: 'Compras', href: '/compras', icon: ShoppingCartIcon },
  { name: 'Inventario', href: '/inventario', icon: CubeIcon },
  {
    name: 'Tesorería', href: '/tesoreria', icon: BanknotesIcon,
    children: [
      { name: 'Cuentas por cobrar', href: '/tesoreria/cuentas-por-cobrar' },
      { name: 'Cuentas por pagar', href: '/tesoreria/cuentas-por-pagar' },
      { name: 'Proyecciones', href: '/tesoreria/proyecciones' },
    ],
  },
  // { name: 'Contabilidad', href: '/contabilidad', icon: BookOpenIcon },
  {
    name: 'Análisis', href: '/analisis', icon: ChartBarIcon,
    children: [
      { name: 'Ventas vs compras', href: '/analisis/ventas-vs-compras' },
      { name: 'Capital inmovilizado', href: '/analisis/capital-inmovilizado' },
      { name: 'Rotación de inventarios', href: '/analisis/rotacion-inventario' },
    ],
  },
  { name: 'Márgenes', href: '/margenes', icon: TagIcon },
  // { name: 'SAT', href: '/sat', icon: DocumentCheckIcon },
  { name: 'Asistente IA', href: '/asistente', icon: ChatBubbleLeftRightIcon },
  { name: 'Insights de IA', href: '/insights', icon: SparklesIcon },
  { name: 'Agentes IA', href: '/log-actividades', icon: CpuChipIcon },
  { name: 'Usuarios', href: '/usuarios', icon: UsersIcon },
]

// Menú según los permisos del rol: se ocultan las páginas sin acceso; si el rol no
// puede abrir la página de una sección pero sí alguna hija (p. ej. Cobranza en
// Tesorería → solo CxC), la sección apunta a su primera hija visible.
function menuDelRol(puedeVer) {
  return navigation.flatMap(item => {
    const children = (item.children || []).filter(c => puedeVer(c.href))
    const propia = puedeVer(item.href)
    if (!propia && children.length === 0) return []
    return [{ ...item, href: propia ? item.href : children[0].href, children: propia ? children : children.slice(1) }]
  })
}

// Los subitems se muestran cuando se está en la sección (la página o una de sus hijas)
// Una sección queda activa también en sus subpáginas (p. ej. /tesoreria/cuentas-por-cobrar)
const dentroDe = (pathname, href) => pathname === href || (href !== '/' && pathname.startsWith(href + '/'))
const enSeccionDe = (pathname) => (item) =>
  dentroDe(pathname, item.href) || (item.children || []).some(c => dentroDe(pathname, c.href))

export default function DashboardLayout({ children }) {
  const location = useLocation()
  const enSeccion = enSeccionDe(location.pathname)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout, isAdmin, puede, puedeVer } = useAuth()
  const menu = menuDelRol(puedeVer)

  const handleLogout = async () => {
    await logout()
    window.location.href = '/login'
  }

  // Obtener iniciales del usuario
  const getInitials = (name) => {
    if (!name) return 'U'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)]">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div 
            className="fixed inset-0 bg-black/20" 
            onClick={() => setSidebarOpen(false)} 
          />
          <div className="fixed inset-y-0 left-0 w-64 bg-white border-r border-[var(--border-default)]">
            <div className="flex h-20 items-center justify-between px-4 border-b border-[var(--border-default)]">
              <div className="flex items-center gap-3">
                <img src="/logo.jpg" alt="Lavandería El Siglo" className="h-14 w-auto object-contain rounded" />
              </div>
              <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-[var(--bg-secondary)] rounded">
                <XMarkIcon className="w-5 h-5 text-[var(--text-secondary)]" />
              </button>
            </div>
            
            <nav className="p-2 space-y-1">
              {menu.map((item) => {
                const isActive = dentroDe(location.pathname, item.href) && !(item.children || []).some(c => dentroDe(location.pathname, c.href))
                return (
                  <div key={item.name} className="space-y-1">
                  <Link
                    to={item.href}
                    className={isActive ? 'nav-link-active' : 'nav-link'}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                  {item.children && enSeccion(item) && item.children.map(c => (
                    <Link
                      key={c.href}
                      to={c.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`${dentroDe(location.pathname, c.href) ? 'nav-link-active' : 'nav-link'} ml-8 py-1.5 text-sm`}
                    >
                      <span>{c.name}</span>
                    </Link>
                  ))}
                  </div>
                )
              })}
            </nav>

            {/* Mobile user section */}
            <div className="p-4 border-t border-[var(--border-default)]">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-secondary)]">
                <div className="w-8 h-8 rounded-full bg-[#001639] flex items-center justify-center">
                  <span className="text-white text-xs font-medium">{getInitials(user?.nombre)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user?.nombre || 'Usuario'}</p>
                  <p className="text-xs text-[var(--text-muted)]">{NOMBRE_ROL[user?.rol] || user?.rol}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white border-r border-[var(--border-default)]">
          {/* Logo */}
          <div className="flex items-center h-20 px-5 border-b border-[var(--border-default)]">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.jpg" alt="Lavandería El Siglo" className="h-16 w-auto object-contain rounded" />
            </Link>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-2 space-y-1">
            {menu.map((item) => {
              const isActive = dentroDe(location.pathname, item.href) && !(item.children || []).some(c => dentroDe(location.pathname, c.href))
              return (
                <div key={item.name} className="space-y-1">
                <Link
                  to={item.href}
                  className={isActive ? 'nav-link-active' : 'nav-link'}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span>{item.name}</span>
                  {item.name === 'Agentes IA' && (
                    <span className="ml-auto w-2 h-2 rounded-full bg-[var(--success)]"></span>
                  )}
                </Link>
                {item.children && enSeccion(item) && item.children.map(c => (
                  <Link
                    key={c.href}
                    to={c.href}
                    className={`${dentroDe(location.pathname, c.href) ? 'nav-link-active' : 'nav-link'} ml-8 py-1.5 text-sm`}
                  >
                    <span>{c.name}</span>
                  </Link>
                ))}
                </div>
              )
            })}
          </nav>
          
          {/* Footer */}
          <div className="p-4 border-t border-[var(--border-default)]">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-secondary)]">
              <div className="w-8 h-8 rounded-full bg-[#001639] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-medium">{getInitials(user?.nombre)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user?.nombre || 'Usuario'}</p>
                <p className="text-xs text-[var(--text-muted)]">{NOMBRE_ROL[user?.rol] || user?.rol}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[var(--border-default)]">
          <div className="flex items-center justify-between h-14 px-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 -ml-2 text-[var(--text-secondary)] lg:hidden hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] rounded"
              >
                <Bars3Icon className="w-5 h-5" />
              </button>
              
              <nav className="hidden sm:flex items-center gap-2 text-sm">
                <span className="text-[var(--text-muted)]">GT</span>
                <span className="text-[var(--border-strong)]">/</span>
                <span className="text-[var(--text-primary)] font-medium">
                  {/* Páginas de detalle (/ventas/vendedor/014) toman el nombre de su sección */}
                  {navigation.flatMap(n => [n, ...(n.children || [])])
                    .filter(n => n.href === location.pathname || (n.href !== '/' && location.pathname.startsWith(`${n.href}/`)))
                    .sort((a, b) => b.href.length - a.href.length)[0]?.name || 'Dashboard'}
                </span>
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <UltimaActualizacion />

              {/* Filtro de fechas global: siempre visible, aplica a todos los módulos */}
              <FiltroPeriodo />

              {/* User badge */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-default)]">
                <UserCircleIcon className="w-4 h-4 text-[var(--text-muted)]" />
                <span className="text-xs font-medium text-[var(--text-primary)]">{user?.nombre || 'Usuario'}</span>
                {isAdmin && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#001639] text-white rounded-full">ADMIN</span>
                )}
              </div>

              <div className="hidden xl:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--success-bg)]">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--success)]"></span>
                <span className="text-xs font-medium text-[var(--success)]">Sistema operativo</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 animate-fade-in">
          {children}
        </main>
      </div>
      
      {/* Widget flotante: en todas las paginas menos en la pagina completa del asistente */}
      {location.pathname !== '/asistente' && puede('asistente') && <AgentChat />}
    </div>
  )
}
